import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import { useLogoutMutation } from '../GraphQL/Logout.generated';

import type { useCustomerData } from './useCustomerData';

export const useSignOut = (props: UseSignOutProps) => {
  const { customer } = props;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  const [signOutResponse, signOutMutation] = useLogoutMutation();
  const signOutResponseData = signOutResponse.data?.logout;

  const isSigningOut = signOutResponse.fetching;
  const isSignedOut = !customer?.id;

  // ─────────────────────── Telemetry ──────────────────────────────────

  const { track } = useTelemetry();

  // ────────────────────────────────────────────────────────────────────

  const signOut = useCallback(async (): Promise<boolean> => {
    if (!customer?.id) return false;

    const response = await signOutMutation({
      input: { customerId: customer.id },
    });
    const logoutData = response?.data?.logout;
    const hasError = logoutData?.__typename === 'ValidationError' || response?.error;

    if (!logoutData || hasError) {
      const userError = formatMessage(messages.accountSignOutError);
      const systemError = getUrqlError(response.error);

      track('logout.failure', { userError, systemError });
      addNoticeBanner({ text: userError, palette: 'caution' });

      return false;
    }

    track('logout.success');
    track('account.logged_out_view');

    return true;
  }, [customer?.id, signOutMutation, formatMessage, track, addNoticeBanner]);

  // ────────────────────────────────────────────────────────────────────

  return { signOutResponseData, isSigningOut, isSignedOut, signOut };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseSignOutProps = Pick<ReturnType<typeof useCustomerData>, 'customer'>;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountSignOutError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Account | Sign Out | Error',
  },
});
